import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import type { Lab, LabResource } from "../lib/supabase";
import LabForm from "../components/labs/LabForm";
import toast from "react-hot-toast";
import LoadingScreen from "../components/ui/LoadingScreen";
import { ENTITY_NAMES } from "../constants/entities";
import SmartBackButton from "../components/ui/SmartBackButton";

interface UpdateLab {
  title: string | null;
  description: string | null;
  order_number: number;
  learning_outcomes: string[];
  mindfulness_topic: string | null;
  coding_topic: string | null;
  objective: string | null;
  resources?: LabResource[];
}

interface InsertLabResource extends Omit<LabResource, "id"> {
  lab_id: string;
}

const EditLab: React.FC = () => {
  const { seriesId, labId } = useParams<{ seriesId: string; labId: string }>();
  const [lab, setLab] = useState<Lab | null>(null);
  const [seriesType, setSeriesType] = useState<string>("series");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLab = async () => {
      try {
        if (!labId) return;

        // Fetch both lab and its resources
        const { data: labData, error: labError } = await supabase
          .from("labs")
          .select("*, resources:lab_resources(*)")
          .eq("id", labId)
          .single();

        if (labError) throw labError;
        setLab(labData);
      } catch (error) {
        console.error("Error fetching lab:", error);
        toast.error("Something went wrong.");
      } finally {
        setIsFetching(false);
      }
    };

    fetchLab();
  }, [labId]);

  useEffect(() => {
    const fetchSeriesType = async () => {
      if (!seriesId) return;
      const { data } = await supabase
        .from("series")
        .select("type")
        .eq("id", seriesId)
        .single();
      if (data?.type) setSeriesType(data.type);
    };

    fetchSeriesType();
  }, [seriesId]);

  const handleUpdateLab = async (data: UpdateLab) => {
    try {
      setIsLoading(true);

      if (!labId) return;

      // Update the lab details
      const { error: labError } = await supabase
        .from("labs")
        .update({
          title: data.title,
          description: data.description,
          order_number: data.order_number,
          learning_outcomes: data.learning_outcomes,
          series_id: seriesId,
          mindfulness_topic: data.mindfulness_topic,
          coding_topic: data.coding_topic,
          objective: data.objective,
        })
        .eq("id", labId);

      if (labError) throw labError;

      // Handle resources update
      if (data.resources) {
        // Get current resources to handle file cleanup
        const { data: currentResources } = await supabase
          .from("lab_resources")
          .select("*")
          .eq("lab_id", labId);

        // Delete files that are no longer referenced
        if (currentResources) {
          const currentFilePaths = currentResources
            .filter((r: LabResource) => r.type === "file")
            .map((r: LabResource) => r.file_path);
          const newFilePaths = data.resources
            .filter((r: LabResource) => r.type === "file")
            .map((r: LabResource) => r.file_path);

          const filesToDelete = currentFilePaths.filter(
            (path): path is string =>
              path !== null &&
              path !== undefined &&
              !newFilePaths.includes(path)
          );

          if (filesToDelete.length > 0) {
            await supabase.storage.from("lab-resources").remove(filesToDelete);
          }
        }

        // Delete all existing resources from the database
        const { error: deleteError } = await supabase
          .from("lab_resources")
          .delete()
          .eq("lab_id", labId);

        if (deleteError) throw deleteError;

        // Then insert new resources
        if (data.resources.length > 0) {
          const resources: InsertLabResource[] = data.resources.map(
            (resource: LabResource) => ({
              ...resource,
              lab_id: labId,
            })
          );

          const { error: resourceError } = await supabase
            .from("lab_resources")
            .insert(resources);

          if (resourceError) throw resourceError;
        }
      }

      toast.success("Saved.");
      navigate(`/series/${seriesId}/labs/${labId}`);
    } catch (error) {
      console.error("Error updating lab:", error);
      toast.error("Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingScreen />;
  if (!lab) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-bold mb-2">{seriesType === "camp" ? "Day" : ENTITY_NAMES.LAB} Not Found</h2>
        <SmartBackButton
          fallbackPath={`/series/${seriesId}`}
          className="btn btn-primary"
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <SmartBackButton
          fallbackPath={`/series/${seriesId}/labs/${labId}`}
          className="text-link hover:text-hover flex items-center"
        >
          Back to {seriesType === "camp" ? "Day" : "Lab"} Details
        </SmartBackButton>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h1>
              Edit {seriesType === "camp" ? "Day" : ENTITY_NAMES.LAB}: {lab.title}
            </h1>
          </div>

          <LabForm
            defaultValues={lab}
            onSubmit={handleUpdateLab}
            isLoading={isLoading}
            onCancel={() => navigate(`/series/${seriesId}/labs/${labId}`)}
          />
        </div>
      </div>
    </div>
  );
};

export default EditLab;
